'use client';

import {
  CardStackPlusIcon,
  ChatBubbleIcon,
  EnterIcon,
  ExitIcon,
  PinLeftIcon,
} from '@radix-ui/react-icons';
import clsx from 'clsx';
import Cookies from 'js-cookie';
import Link from 'next/link';

import { useUser } from '@/api/api-hooks/auth-hooks';
import { ISidebarItem, SidebarItem } from '@/components/elements/Sidebar/sidebar-item';
import { SIDEBAR_COOKIE_NAME, useSidebar } from '@/components/elements/Sidebar/sidebar-provider';
import AllChatsIcon from '@/components/icons/AllChatsIcon';
import PromptsIcon from '@/components/icons/PromptsIcon';
import SignUpIcon from '@/components/icons/SignUpIcon';
import SourcesIcon from '@/components/icons/SourcesIcon';
import SponsorsIcon from '@/components/icons/SponsorsIcon';
import UsersIcon from '@/components/icons/UsersIcon';
import AppButton from '@/components/ui/Button';
import { useAuth } from '@/hooks/useAuth';
import useLoginDrawer from '@/hooks/useLoginDrawerStore';
import { getInitials } from '@/utils/utils-functions';

export function Sidebar() {
  const { isCollapsed, setIsCollapsed } = useSidebar();

  const { data: user } = useUser();

  const handleSidebarToggle = () => {
    Cookies.set(SIDEBAR_COOKIE_NAME, JSON.stringify(!isCollapsed));
    setIsCollapsed((prev) => !prev);
  };
  const { openDrawer } = useLoginDrawer();
  const { isLogin, logoutFromStore } = useAuth();

  const { isAdmin } = useAuth();
  const primaryItems: ISidebarItem[] = [
    {
      name: 'Chat',
      href: '/chat',
      icon: <ChatBubbleIcon className="h-5 w-5" />,
      visible: true,
      matchMode: 'startsWith',
    },
    {
      name: 'Subscription',
      href: '/subscription',
      icon: <CardStackPlusIcon className="h-5 w-5" />,
      visible: true,
      matchMode: 'exact',
    },
    {
      name: 'Sources',
      href: 'admin/sources',
      icon: <SourcesIcon />,
      visible: isAdmin,
      matchMode: 'startsWith',
    },
    {
      name: 'All Chats',
      href: 'admin/chats',
      icon: <AllChatsIcon className="text-inherit" />,
      visible: isAdmin,
      matchMode: 'startsWith',
    },
    {
      name: 'Prompts',
      href: 'admin/prompts',
      icon: <PromptsIcon />,
      visible: isAdmin,
      matchMode: 'startsWith',
    },
    {
      name: 'Sponsors',
      href: 'admin/sponsors',
      icon: <SponsorsIcon />,
      visible: isAdmin,
    },
    {
      name: 'Users',
      href: 'admin/users',
      icon: <UsersIcon className="h-5 w-5" />,
      visible: isAdmin,
      matchMode: 'startsWith',
    },
  ];

  const openSignUpDrawer = () => {
    openDrawer('register');
  };

  const openSignInDrawer = () => {
    openDrawer('login');
  };

  return (
    <div
      id="desktop-sidebar"
      className={clsx(
        'fixed inset-y-0 z-50 flex flex-col overflow-hidden bg-black transition-all',
        isCollapsed
          ? 'peer is-collapsed top-[64px] -left-[100%] w-14 md:top-0 md:left-0 md:w-20'
          : 'top-[64px] left-0 w-50 md:top-0 md:w-[260px]',
      )}
    >
      <div className="flex grow flex-col gap-y-8 px-2 pt-8 md:px-4 md:pt-0">
        <div className={clsx('mt-6 hidden h-14 shrink-0 items-center justify-between md:flex')}>
          {isCollapsed ? (
            <button onClick={handleSidebarToggle}>
              <img src="/images/small-logo.svg" alt="" className="h-6 w-25.25 md:h-8 md:w-33.75" />
            </button>
          ) : (
            <Link href="/">
              <img src="/images/logo.svg" alt="" className="h-6 w-25.25 md:h-8 md:w-33.75" />
            </Link>
          )}
          {!isCollapsed && (
            <button
              className="hidden w-10 items-center justify-center md:flex"
              onClick={handleSidebarToggle}
              aria-label="Hide menu"
            >
              {!isCollapsed && <PinLeftIcon className="h-5 w-5 text-white" />}
            </button>
          )}
        </div>
        <nav className="flex h-full flex-1 flex-col justify-between py-2">
          <ul role="list" className="flex flex-1 flex-col gap-y-1">
            {primaryItems.map(
              ({ visible = true, render, ...item }) =>
                visible && (
                  <li key={item.name}>
                    {render ? render() : <SidebarItem {...item} minimized={isCollapsed} />}
                  </li>
                ),
            )}
          </ul>
          {!isLogin && (
            <div className="flex flex-col gap-y-2 pb-4">
              <AppButton
                variant="secondary"
                className="hover:border-primary h-[38px] w-full justify-center border-white hover:!bg-transparent"
                labelClassName="text-center text-white "
                onClick={openSignInDrawer}
              >
                {isCollapsed ? <EnterIcon className="h-5 w-5 text-white" /> : 'Log in'}
              </AppButton>
              <AppButton
                className="h-[38px] w-full justify-center"
                labelClassName="text-center"
                variant="primary"
                onClick={openSignUpDrawer}
              >
                {isCollapsed ? <SignUpIcon /> : 'Sign up'}
              </AppButton>
            </div>
          )}
          {isLogin && (
            <div className="flex gap-y-2 pb-4">
              <div
                className={clsx('flex w-full justify-between', isCollapsed && 'flex-col gap-y-2')}
              >
                <div className="flex items-center">
                  <div className="bg-primary mr-4 min-w-10 rounded-sm p-2">
                    <p className="text-text-primary text-header-button text-center uppercase">
                      {getInitials(user?.name)}
                    </p>
                  </div>
                  {!isCollapsed && <p className="text-white">{user?.name}</p>}
                </div>
                <button className="p-2" onClick={logoutFromStore}>
                  <ExitIcon className="h-5 w-5 text-white" />
                </button>
              </div>
            </div>
          )}
        </nav>
      </div>
    </div>
  );
}
